import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

/**
 * CountdownTimer Component
 * Displays a countdown timer for cart item expiration
 * @param {string} expiredTime - ISO format datetime string
 * @param {function} onExpire - Callback function when timer reaches 0
 */
export const CountdownTimer = ({ expiredTime, onExpire }) => {
    const [timeLeft, setTimeLeft] = useState(null);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        if (!expiredTime) return;

        const calculateTimeLeft = () => {
            const now = new Date();
            const expiry = new Date(expiredTime);
            const difference = expiry - now;

            if (difference <= 0) {
                setIsExpired(true);
                setTimeLeft(null);
                if (onExpire && typeof onExpire === 'function') {
                    onExpire();
                }
                return null;
            }

            const hours = Math.floor(difference / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);

            return { hours, minutes, seconds, total: difference };
        };

        // Initial calculation
        const initial = calculateTimeLeft();
        setTimeLeft(initial);

        // Update every second
        const timer = setInterval(() => {
            const time = calculateTimeLeft();
            setTimeLeft(time);
        }, 1000);

        return () => clearInterval(timer);
    }, [expiredTime, onExpire]);

    if (!expiredTime || isExpired) {
        return null;
    }

    if (!timeLeft) {
        return null;
    }

    const formatTime = () => {
        const { hours, minutes, seconds } = timeLeft;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Determine urgency level for styling
    const getUrgencyClass = () => {
        const totalMinutes = Math.floor(timeLeft.total / (1000 * 60));

        if (totalMinutes <= 2) {
            return 'text-red-600 bg-red-50 border-red-200';
        } else if (totalMinutes <= 5) {
            return 'text-orange-600 bg-orange-50 border-orange-200';
        }
        return 'text-blue-600 bg-blue-50 border-blue-200';
    };

    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${getUrgencyClass()} text-sm font-medium`}>
            <Clock className="w-4 h-4" />
            <div>
                <span className="text-xs opacity-80">Lakukan Pembayaran sebelum: </span>
                <span className="font-bold ml-1">{formatTime()}</span>
            </div>
        </div>
    );
};

export default CountdownTimer;
